/**
 * Configuration interface for password complexity validation
 */
export interface PasswordComplexityConfig {
    /** CSS classes for successful validation (space-separated) */
    successClass?: string;
    /** CSS classes for unsuccessful validation (space-separated) */
    unsuccessfulClass?: string;
    /** CSS class for icon margin */
    iconMarginClass?: string;
    /** CSS class for icon */
    iconClass?: string;
    /** Requirements for password validation */
    requirements?: Array<{
        id: string;
        text: string;
        regex: RegExp;
    }>;
}

/**
 * Password complexity validation functionality
 * Checks password against the required complexity rules and provides visual feedback
 */
export class PasswordComplexity {
    private passwordField: HTMLInputElement;
    private validationContainer: HTMLElement | null;

    // Default configuration values
    private static readonly DEFAULT_SUCCESS_CLASS = 'text-success';
    private static readonly DEFAULT_UNSUCCESSFUL_CLASS = 'text-info opacity-25';
    private static readonly DEFAULT_ICON_MARGIN_CLASS = 'me-xxs';
    private static readonly DEFAULT_ICON_CLASS = 'bi bi-check-circle-fill';
    private static readonly DEFAULT_REQUIREMENTS = [
        { id: 'length', text: 'At least 6 characters in length', regex: /.{6,}/ },
        { id: 'number', text: 'At least 1 number', regex: /\d/ },
        { id: 'lowercase', text: 'At least 1 lowercase letter', regex: /[a-z]/ },
        { id: 'uppercase', text: 'At least 1 uppercase letter', regex: /[A-Z]/ }
    ];

    // Configuration with applied defaults
    private config: Required<PasswordComplexityConfig>;

    /**
     * Initialize password complexity validation across the document
     * @param config Optional configuration to override defaults
     */
    public static initialize(config?: PasswordComplexityConfig): void {
        document.addEventListener('DOMContentLoaded', () => {
            // Find password input fields using data attribute
            const passwordFields = document.querySelectorAll('input[data-validate="password"]');

            passwordFields.forEach(passwordField => {
                // Get the validation container ID from data attribute
                const validationContainerId = (passwordField as HTMLElement).dataset.passwordValidationMessage;

                if (validationContainerId) {
                    const validationContainer = document.getElementById(validationContainerId);

                    if (validationContainer) {
                        new PasswordComplexity(
                            passwordField as HTMLInputElement,
                            validationContainer,
                            config
                        );
                    }
                }
            });
        });
    }

    /**
     * Create a new password complexity validator for a specific field
     * @param passwordField Input field to validate
     * @param validationContainer Container to show validation status
     * @param config Optional configuration to override defaults
     */
    constructor(
        passwordField: HTMLInputElement,
        validationContainer: HTMLElement,
        config?: PasswordComplexityConfig
    ) {
        this.passwordField = passwordField;
        this.validationContainer = validationContainer;

        // Apply defaults to each config property individually
        this.config = {
            successClass: config?.successClass ?? PasswordComplexity.DEFAULT_SUCCESS_CLASS,
            unsuccessfulClass: config?.unsuccessfulClass ?? PasswordComplexity.DEFAULT_UNSUCCESSFUL_CLASS,
            iconMarginClass: config?.iconMarginClass ?? PasswordComplexity.DEFAULT_ICON_MARGIN_CLASS,
            iconClass: config?.iconClass ?? PasswordComplexity.DEFAULT_ICON_CLASS,
            requirements: config?.requirements ?? PasswordComplexity.DEFAULT_REQUIREMENTS
        };

        // Create and inject the validation UI
        this.createValidationUI();

        // Add event listeners for input changes
        this.passwordField.addEventListener('input', this.validatePassword.bind(this));

        // Initial validation
        this.validatePassword();
    }

    /**
     * Create the validation UI elements inside the designated container
     */
    private createValidationUI(): void {
        if (!this.validationContainer) return;

        // Clear existing content
        this.validationContainer.innerHTML = '';

        // Create each requirement element
        this.config.requirements.forEach(req => {
            const requirementItem = document.createElement('div');
            requirementItem.className = 'requirement-item d-flex align-items-center mb-xs';
            requirementItem.id = `requirement-${req.id}`;

            // Create the icon element
            const icon = document.createElement('i');
            icon.id = `${req.id}-check`;
            icon.className = `${this.config.iconClass} ${this.config.iconMarginClass} ${this.config.unsuccessfulClass}`;

            // Create the text element
            const textSpan = document.createElement('span');
            textSpan.textContent = req.text;

            // Append both to the requirement item
            requirementItem.appendChild(icon);
            requirementItem.appendChild(textSpan);

            this.validationContainer?.appendChild(requirementItem);
        });
    }

    /**
     * Validate the password against complexity requirements
     */
    private validatePassword(): void {
        const password = this.passwordField.value;
        let allValid = true;

        // Check each requirement
        this.config.requirements.forEach(req => {
            const valid = req.regex.test(password);
            this.updateRequirementStatus(req.id, valid);

            if (!valid) {
                allValid = false;
            }
        });

        // Optionally enable/disable the submit button based on validity
        const submitButton = document.querySelector('input[type="submit"]') as HTMLInputElement;
        if (submitButton) {
            submitButton.disabled = !allValid;
        }
    }

    /**
     * Update the visual status of a requirement
     * @param requirementId ID of the requirement to update
     * @param valid Whether the requirement is valid
     */
    private updateRequirementStatus(requirementId: string, valid: boolean): void {
        const checkIcon = document.getElementById(`${requirementId}-check`);
        if (!checkIcon) return;

        // Split class strings into arrays for proper add/remove operations
        const successClasses = this.config.successClass.split(/\s+/).filter(cls => cls.length > 0);
        const unsuccessfulClasses = this.config.unsuccessfulClass.split(/\s+/).filter(cls => cls.length > 0);

        if (valid) {
            // Remove all unsuccessful classes
            unsuccessfulClasses.forEach(cls => {
                checkIcon.classList.remove(cls);
            });

            // Add all success classes
            successClasses.forEach(cls => {
                checkIcon.classList.add(cls);
            });
        } else {
            // Remove all success classes
            successClasses.forEach(cls => {
                checkIcon.classList.remove(cls);
            });

            // Add all unsuccessful classes
            unsuccessfulClasses.forEach(cls => {
                checkIcon.classList.add(cls);
            });
        }
    }
}

PasswordComplexity.initialize();